import { useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import FileDownloadRounded from "@mui/icons-material/FileDownloadRounded";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import SendRounded from "@mui/icons-material/SendRounded";

const GREETING = "Hi! I've analyzed your data from the Explore tab. How can I help? You can also generate a report.";

const STARTER_CHIPS = [
	{ label: "Generate Insights Report", question: "" },
	{ label: "Add news to top keywords", question: "Add news headlines to top trending keywords" },
	{ label: "Give more details on the top trending topic", question: "Give more details on the top trending topic" },
];

const quietBtnSx = {
	textTransform: "none",
	fontWeight: 600,
	fontSize: 12.5,
	color: grey[800],
	borderRadius: 1.5,
};

const htmlToMarkdown = (html = "") => {
	let md = String(html);
	md = md.replace(/<h1[^>]*>(.*?)<\/h1>/gi, "# $1\n\n");
	md = md.replace(/<h2[^>]*>(.*?)<\/h2>/gi, "## $1\n\n");
	md = md.replace(/<h3[^>]*>(.*?)<\/h3>/gi, "### $1\n\n");
	md = md.replace(/<strong[^>]*>(.*?)<\/strong>/gi, "**$1**");
	md = md.replace(/<b[^>]*>(.*?)<\/b>/gi, "**$1**");
	md = md.replace(/<em[^>]*>(.*?)<\/em>/gi, "*$1*");
	md = md.replace(/<i[^>]*>(.*?)<\/i>/gi, "*$1*");
	md = md.replace(/<ul[^>]*>/gi, "\n");
	md = md.replace(/<\/ul>/gi, "\n");
	md = md.replace(/<li[^>]*>(.*?)<\/li>/gi, "- $1\n");
	md = md.replace(/<p[^>]*>/gi, "\n\n");
	md = md.replace(/<\/p>/gi, "");
	md = md.replace(/<br\s*\/?>/gi, "\n");
	md = md.replace(/<[^>]+>/g, "");
	md = md.replace(/&nbsp;/g, " ");
	md = md.replace(/&amp;/g, "&");
	md = md.replace(/&lt;/g, "<");
	md = md.replace(/&gt;/g, ">");
	return md.trim();
};

const isHtml = (text = "") => /<[a-z][\s\S]*>/i.test(String(text).trim());

const hydrateChat = (insights) => {
	if (Array.isArray(insights?.chat) && insights.chat.length) return insights.chat;
	if (insights?.report) {
		return [
			{ role: "user", text: "Generate Insights Report" },
			{ role: "model", text: insights.report, isReport: true },
		];
	}
	return [];
};

const GigaInsightsView = ({ run, busy, onAsk, onReset }) => {
	const insights = run?.insights || {};
	const report = insights.report || "";
	const suggestions = insights.suggestions || [];
	const chat = useMemo(() => hydrateChat(insights), [insights]);
	const [draft, setDraft] = useState("");
	const [pendingUser, setPendingUser] = useState("");
	const scrollerRef = useRef(null);
	const generating = busy === "insights";
	const hasExplore = Boolean(run?.ideas?.length);
	const showStarters = hasExplore && !generating && !chat.length;
	const lastChatUser = [...chat].reverse().find((message) => message.role === "user");
	const showPendingUser = Boolean(pendingUser) && generating && lastChatUser?.text !== pendingUser;

	useEffect(() => {
		if (!generating) setPendingUser("");
	}, [generating]);

	useEffect(() => {
		if (scrollerRef.current) {
			scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
		}
	}, [chat, generating, pendingUser]);

	const ask = (question = "") => {
		if (!hasExplore || generating) return;
		setPendingUser(question || "Generate Insights Report");
		onAsk({ language: "English", question });
	};

	const sendDraft = () => {
		const question = draft.trim();
		if (!question) return;
		setDraft("");
		ask(question);
	};

	const downloadReport = () => {
		const messages = chat.length
			? chat
			: report
			? [{ role: "user", text: "Generate Insights Report" }, { role: "model", text: report, isReport: true }]
			: [];
		if (!messages.length) return;
		const body = messages
			.filter((message) => String(message.text || "").trim())
			.map((message) => {
				const label = message.role === "user" ? "User" : message.isReport ? "Report" : "Assistant";
				const text = isHtml(message.text) ? htmlToMarkdown(message.text) : String(message.text || "").trim();
				return `## ${label}\n\n${text}`;
			})
			.join("\n\n");
		const markdown = `# Insights — ${run?.name || "GIGA"}\n\n${body}\n`;
		const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `${String(run?.name || "giga-insights").replace(/[^\w\-]+/g, "_").slice(0, 40)}_insights.md`;
		link.click();
		URL.revokeObjectURL(url);
	};

	if (!hasExplore) {
		return <Alert severity="info">Run Explore before generating insights.</Alert>;
	}

	return (
		<Stack spacing={2}>
			<Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
				<Box>
					<Typography fontWeight={800} fontSize={18} color={grey[900]}>
						Insights
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Chat with the briefing from this run’s keyword data.
					</Typography>
				</Box>
				<Stack direction="row" alignItems="center" spacing={0.5}>
					{(Boolean(report) || chat.length > 0) && (
						<Tooltip title="Download full chat as markdown">
							<IconButton size="small" onClick={downloadReport} sx={{ color: grey[700] }}>
								<FileDownloadRounded fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					<Tooltip title="Reset Insights">
						<span>
							<IconButton size="small" onClick={onReset} disabled={generating || (!chat.length && !report)} sx={{ color: grey[700] }}>
								<DeleteOutlineRounded fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Stack>
			</Stack>

			<Paper
				variant="outlined"
				sx={{
					p: 1.5,
					borderRadius: 2,
					bgcolor: grey[50],
					minHeight: 280,
					maxHeight: 560,
					overflowY: "auto",
				}}
				ref={scrollerRef}>
				<Stack spacing={1.25}>
					<Box sx={{ alignSelf: "flex-start", maxWidth: "80%" }}>
						<Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: "#fff", border: `1px solid ${grey[200]}` }}>
							<Typography fontSize={13.5} color={grey[800]} sx={{ lineHeight: 1.55 }}>
								{GREETING}
							</Typography>
						</Paper>
					</Box>
					{chat.map((message, index) => {
						const user = message.role === "user";
						const reportBubble = Boolean(message.isReport);
						const text = message.text || "";
						return (
							<Box key={`${message.role}-${index}`} sx={{ alignSelf: user ? "flex-end" : "stretch", maxWidth: reportBubble ? "100%" : "80%" }}>
								{reportBubble ? (
									<Paper
										elevation={0}
										sx={{
											p: 2.5,
											borderRadius: 2,
											bgcolor: "#fff",
											border: `1px solid ${grey[200]}`,
											"& h1, & h2": { mt: 0, mb: 1.5, fontSize: 20, fontWeight: 800 },
											"& h3": { mt: 2, mb: 1, fontSize: 15, fontWeight: 700 },
											"& p, & li": { fontSize: 14.5, lineHeight: 1.7, color: "#334155" },
											"& ul": { pl: 2.5 },
										}}
										dangerouslySetInnerHTML={{ __html: text }}
									/>
								) : user ? (
									<Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: "#175CD3", color: "#fff" }}>
										<Typography fontSize={13.5} sx={{ lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
											{text}
										</Typography>
									</Paper>
								) : isHtml(text) ? (
									<Paper
										elevation={0}
										sx={{
											px: 1.75,
											py: 1.25,
											borderRadius: 2,
											bgcolor: "#fff",
											border: `1px solid ${grey[200]}`,
											"& p, & li": { fontSize: 14, lineHeight: 1.65, color: "#334155", m: 0 },
											"& p + p": { mt: 1 },
											"& ul": { pl: 2.25, my: 0.75 },
										}}
										dangerouslySetInnerHTML={{ __html: text }}
									/>
								) : (
									<Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: "#fff", border: `1px solid ${grey[200]}` }}>
										<Typography fontSize={13.5} color={grey[800]} sx={{ lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
											{text}
										</Typography>
									</Paper>
								)}
							</Box>
						);
					})}
					{showPendingUser && (
						<Box sx={{ alignSelf: "flex-end", maxWidth: "80%" }}>
							<Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: "#175CD3", color: "#fff" }}>
								<Typography fontSize={13.5} sx={{ lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
									{pendingUser}
								</Typography>
							</Paper>
						</Box>
					)}
					{generating && (
						<Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5 }}>
							<CircularProgress size={14} />
							<Typography fontSize={13} color="text.secondary">
								{report || chat.length ? "Thinking…" : "Generating report…"}
							</Typography>
						</Stack>
					)}
				</Stack>
			</Paper>

			{suggestions.length > 0 && !generating && (
				<Stack direction="row" flexWrap="wrap" gap={1}>
					{suggestions.map((item) => (
						<Chip
							key={item}
							label={item}
							onClick={() => ask(item)}
							sx={{
								height: 30,
								fontWeight: 600,
								bgcolor: "#EFF8FF",
								border: "1px solid #B2DDFF",
								"&:hover": { bgcolor: "#D1E9FF" },
							}}
						/>
					))}
				</Stack>
			)}

			{showStarters && (
				<Stack direction="row" flexWrap="wrap" gap={1}>
					{STARTER_CHIPS.map((chip) => (
						<Chip
							key={chip.label}
							icon={chip.question ? undefined : <LightbulbOutlined sx={{ fontSize: 16 }} />}
							label={chip.label}
							onClick={() => ask(chip.question)}
							sx={{
								height: 32,
								fontWeight: 600,
								bgcolor: "#EFF8FF",
								border: "1px solid #B2DDFF",
								"&:hover": { bgcolor: "#D1E9FF" },
							}}
						/>
					))}
				</Stack>
			)}

			<Stack direction="row" spacing={1}>
				<TextField
					fullWidth
					size="small"
					placeholder="Ask a follow-up question..."
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.shiftKey) {
							e.preventDefault();
							sendDraft();
						}
					}}
					disabled={generating}
					sx={{ bgcolor: "#fff" }}
				/>
				<Button
					variant="contained"
					onClick={sendDraft}
					disabled={generating || !draft.trim()}
					sx={{ ...quietBtnSx, minWidth: 48, px: 1.5, color: "#fff" }}>
					<SendRounded fontSize="small" />
				</Button>
			</Stack>
		</Stack>
	);
};

export default GigaInsightsView;
